import * as v from 'valibot';

/**
 * The software factory's shared vocabulary.
 *
 * Every stage of the factory — triage, code generation, validation, release,
 * documentation, monitoring — hands its output to the next one. The failure
 * mode a factory has that a single agent does not is the *bad handoff*: a step
 * that produced almost-right data, and five steps downstream nobody can tell
 * whether the answer is wrong because the model was wrong or because the shape
 * was. So the handoff is typed, once, here, and every stage validates at its
 * own boundary rather than trusting the stage before it.
 *
 * One work item is threaded through the whole loop. It gains fields as it goes
 * — a PR when code generation finishes, a verdict when validation finishes, a
 * version when release finishes — and it never loses the `signal` it came from,
 * because "why does this change exist" is the question a human asks first and
 * the one an audit trail most often cannot answer.
 */

/** Where a piece of work came from. */
export const SignalSource = v.picklist([
  'github_issue',
  'github_pr',
  /*
   * The trackers. `linear` is the one with a first-class trigger and the
   * fastest path into the factory; it was missing from this list for as long
   * as the list existed, so an item that arrived from a Linear issue had to
   * call itself `manual` — and the audit trail then said a person filed it.
   */
  'linear',
  'jira',
  'slack',
  'sentry',
  'deployment',
  'monitoring',
  'manual',
]);
export type SignalSource = v.InferOutput<typeof SignalSource>;

/**
 * P0 to P3, and nothing finer.
 *
 * Four levels because a triage agent asked for a 1–10 score produces a 7 for
 * everything. Four forces a decision that means something: P0 wakes somebody,
 * P3 waits for a quiet week.
 *
 * Set by triage, never by the person who started the run. Somebody filing a
 * signal is describing what happened, not ranking it against the other
 * eighty items in the queue — and a human guess made before the context is
 * read is a number the agent then has to decide whether to contradict.
 */
export const Priority = v.picklist(['P0', 'P1', 'P2', 'P3']);
export type Priority = v.InferOutput<typeof Priority>;

export const WorkItemState = v.picklist([
  /*
   * Arrived, and nothing has looked at it yet.
   *
   * The state a hand-started run's item is in. It used to be written as
   * `triaged` because that was the first value in the list, which said
   * something untrue about an item no agent had read — and `triaged` is the
   * word every downstream step takes as permission to act.
   */
  'received',
  'triaged',
  'in_progress',
  'in_validation',
  'awaiting_release',
  'released',
  'documented',
  'escalated',
  'rejected',
]);
export type WorkItemState = v.InferOutput<typeof WorkItemState>;

/**
 * The signal as it arrived, with enough context attached that no agent has to
 * go hunting for it.
 *
 * `context` is deliberately free-form and deliberately required to be *full*:
 * an incoming Sentry signal carries the stack trace, the breadcrumbs, the
 * affected user count and first-seen/last-seen, not just an issue id. Context
 * quality at the input decides decision quality everywhere downstream, and a
 * step that has to fetch the trace itself is a step that sometimes does not.
 */
export const Signal = v.object({
  source: SignalSource,
  /** Provider-native id: issue number, Sentry issue id, deployment sha. */
  externalId: v.string(),
  url: v.optional(v.nullable(v.string())),
  title: v.string(),
  body: v.optional(v.nullable(v.string())),
  repo: v.optional(v.nullable(v.string())),
  labels: v.optional(v.array(v.string()), []),
  observedAt: v.string(),
  /** Everything else the source knew, verbatim. */
  context: v.optional(v.record(v.string(), v.unknown()), {}),
});
export type Signal = v.InferOutput<typeof Signal>;

/** What a reviewer sees on a validation verdict, per dimension. */
export const ValidationDimension = v.picklist([
  'correctness',
  'security',
  'tests',
  'performance',
]);
export type ValidationDimension = v.InferOutput<typeof ValidationDimension>;

export const DimensionVerdict = v.object({
  dimension: ValidationDimension,
  /** `pass`, `concerns` and `fail` — three, because a binary hides the middle. */
  verdict: v.picklist(['pass', 'concerns', 'fail']),
  /** 0–100, and only meaningful next to the verdict it explains. */
  confidence: v.pipe(v.number(), v.minValue(0), v.maxValue(100)),
  findings: v.optional(v.array(v.string()), []),
  notes: v.optional(v.string(), ''),
});
export type DimensionVerdict = v.InferOutput<typeof DimensionVerdict>;

/**
 * The gate. Four dimensions, each scored on its own.
 *
 * Scored separately because a single "looks good" is unevaluable: a change that
 * is correct and insecure and one that is secure and wrong both come out as
 * "mostly fine", and the two need opposite responses.
 */
export const ValidationVerdict = v.object({
  workItemId: v.string(),
  pullRequest: v.nullable(v.number()),
  dimensions: v.array(DimensionVerdict),
  /** Derived, not asserted: any `fail` fails the gate. */
  approved: v.boolean(),
  /** Set when the gate wants a person rather than a retry. */
  escalationReason: v.optional(v.nullable(v.string())),
  reviewedAt: v.string(),
});
export type ValidationVerdict = v.InferOutput<typeof ValidationVerdict>;

/** The pull request a code-generation run produced. */
export const ProposedChange = v.object({
  repo: v.string(),
  branch: v.string(),
  pullRequest: v.nullable(v.number()),
  url: v.optional(v.nullable(v.string())),
  filesTouched: v.optional(v.array(v.string()), []),
  testsAdded: v.optional(v.array(v.string()), []),
  /** The flag new behaviour ships behind, when there is one. */
  flagKey: v.optional(v.nullable(v.string())),
  summary: v.string(),
});
export type ProposedChange = v.InferOutput<typeof ProposedChange>;

export const ReleaseRecord = v.object({
  version: v.string(),
  changelog: v.string(),
  deployedAt: v.nullable(v.string()),
  healthy: v.nullable(v.boolean()),
  rolledBack: v.optional(v.boolean(), false),
  rollbackReason: v.optional(v.nullable(v.string())),
});
export type ReleaseRecord = v.InferOutput<typeof ReleaseRecord>;

/**
 * One unit of work, threaded through every stage.
 *
 * The optional tail — `change`, `verdict`, `release` — is the record of how far
 * the item got. An item with a `change` and no `verdict` is sitting in the
 * gate; one with a failing `verdict` and no `release` is the thing that stopped
 * before production, which is the whole point of having a gate.
 */
export const WorkItem = v.object({
  id: v.string(),
  title: v.pipe(v.string(), v.minLength(1)),
  description: v.optional(v.string(), ''),
  /**
   * How bad it is, decided by an agent. Distinct from `confidence`, which is
   * how sure that agent is.
   *
   * Null until something ranks it, and null is a state rather than a gap: an
   * item that arrived a moment ago from a person describing a problem has not
   * been ranked, and defaulting it to `P3` would say it was and say "not
   * urgent". Every path that reaches a pull request sets one — triage on the
   * ordinary route, `receiveWorkItem` on a run started by hand — so a null
   * that survives to the end is a bug with somewhere to look rather than a
   * severity nobody chose.
   */
  priority: v.optional(v.nullable(Priority), null),
  /** 0–100. Triage says how sure it is, and low confidence routes to a human. */
  confidence: v.optional(v.pipe(v.number(), v.minValue(0), v.maxValue(100)), 50),
  state: WorkItemState,
  repo: v.optional(v.nullable(v.string())),
  /** Which agent owns it next: `codeGen`, `documentation`, `monitoring`, `human`. */
  assignedTo: v.optional(v.nullable(v.string())),
  signal: Signal,
  /** Set when triage recognised this as a duplicate of an item it already made. */
  duplicateOf: v.optional(v.nullable(v.string())),
  change: v.optional(v.nullable(ProposedChange)),
  verdict: v.optional(v.nullable(ValidationVerdict)),
  release: v.optional(v.nullable(ReleaseRecord)),
  createdAt: v.string(),
  updatedAt: v.string(),
});
export type WorkItem = v.InferOutput<typeof WorkItem>;

/** What triage produces: the item, plus where it decided to send it. */
export const TriageResult = v.object({
  items: v.array(WorkItem),
  /** Items triage refused to create, and why — a dedupe is not a silent no-op. */
  suppressed: v.optional(
    v.array(v.object({ title: v.string(), reason: v.string() })),
    [],
  ),
  routedTo: v.optional(v.array(v.string()), []),
});
export type TriageResult = v.InferOutput<typeof TriageResult>;

// ------------------------------------------------------------------ monitoring

export const AnomalySeverity = v.picklist(['sev1', 'sev2', 'sev3', 'info']);
export type AnomalySeverity = v.InferOutput<typeof AnomalySeverity>;

export const Anomaly = v.object({
  id: v.string(),
  service: v.string(),
  severity: AnomalySeverity,
  metric: v.string(),
  /** What it is now, and what it was in the comparable window before. */
  observed: v.number(),
  baseline: v.number(),
  description: v.string(),
  firstSeen: v.string(),
});
export type Anomaly = v.InferOutput<typeof Anomaly>;

export const Incident = v.object({
  id: v.string(),
  severity: AnomalySeverity,
  title: v.string(),
  summary: v.string(),
  anomalies: v.optional(v.array(v.string()), []),
  /** The work item monitoring opened so the fix re-enters the primary loop. */
  workItemId: v.optional(v.nullable(v.string())),
  openedAt: v.string(),
  resolvedAt: v.optional(v.nullable(v.string())),
});
export type Incident = v.InferOutput<typeof Incident>;

export const HealthReport = v.object({
  windowMinutes: v.number(),
  /** 0–100, where 100 is "nothing to say". */
  healthScore: v.pipe(v.number(), v.minValue(0), v.maxValue(100)),
  anomalies: v.optional(v.array(Anomaly), []),
  incidents: v.optional(v.array(Incident), []),
  notes: v.optional(v.string(), ''),
  polledAt: v.string(),
});
export type HealthReport = v.InferOutput<typeof HealthReport>;

// --------------------------------------------------------------------- metrics

/**
 * How the factory is doing, in the three numbers that change a decision.
 *
 * Throughput without backlog flatters a factory that is falling behind;
 * backlog without cycle time cannot tell a queue that is growing from one that
 * is merely long. All three, or none.
 */
export const FactoryMetrics = v.object({
  windowHours: v.number(),
  /** Work items released in the window. */
  throughput: v.number(),
  /** Items triaged and not yet released. */
  backlog: v.number(),
  /** Median hours from triage to release, over items that finished in the window. */
  cycleTimeHours: v.nullable(v.number()),
  /** Runs that ended in `failed`, as a share of runs that ended at all. */
  failureRate: v.number(),
  /** Items sitting in `escalated` — the queue a human owns. */
  escalations: v.number(),
  byStage: v.optional(v.record(v.string(), v.number()), {}),
  computedAt: v.string(),
});
export type FactoryMetrics = v.InferOutput<typeof FactoryMetrics>;

// ---------------------------------------------------------------------- agents

/**
 * One agent as the studio lists it.
 *
 * Reef has no agent table and should not gain one: an agent here *is* its
 * instructions (a versioned skill), the connections it may reach, and the
 * workflow steps that run it. This is the projection of those three facts onto
 * one row, computed on read — so an agent cannot drift from the document that
 * defines it, because there is nothing to drift.
 */
export const FactoryAgentSummary = v.object({
  id: v.string(),
  name: v.string(),
  /** One line, from the skill's frontmatter. */
  responsibility: v.string(),
  /** The skill body — what the studio shows and what a human approves. */
  instructions: v.string(),
  skill: v.string(),
  model: v.string(),
  /** Connection mounts the agent's steps carry, e.g. `github`, `sentry`. */
  tools: v.array(v.string()),
  /** Workflow ids with a step that runs this agent. */
  workflows: v.array(v.string()),
  stepCount: v.number(),
  triggeredBy: v.string(),
  produces: v.string(),
});
export type FactoryAgentSummary = v.InferOutput<typeof FactoryAgentSummary>;

// ------------------------------------------------------------------ factories

/**
 * One stage of a factory: a workflow, standing in for a step of a lifecycle.
 *
 * The stage is the unit a person reasons about — "it is in validation" — and
 * the workflow is the unit reef runs. A stage names its workflow rather than
 * containing it, because the workflow document is the thing that is authored,
 * authored and shared; the factory is only an ordering of those documents
 * with the handoffs written down.
 *
 * `states` is what makes a work item placeable on a board: an item whose state
 * is one of them is sitting in this stage. Two stages must not claim the same
 * state, and `validateFactory` says so.
 */
export const FactoryStage = v.object({
  id: v.pipe(v.string(), v.minLength(1)),
  name: v.pipe(v.string(), v.minLength(1)),
  /** The workflow that runs this stage. May be absent from a deployment; the studio draws it as an outline. */
  workflowId: v.pipe(v.string(), v.minLength(1)),
  description: v.string(),
  /** What starts it: the event, the handoff, or the person. Prose, because it is read. */
  entersOn: v.string(),
  /** What it hands to the next stage. */
  produces: v.string(),
  /** Work-item states an item is in while this stage owns it. */
  states: v.optional(v.array(WorkItemState), []),
  /**
   * The step that stops and asks a person, when this stage has one.
   *
   * Named rather than flagged: "release waits at approveRelease" is checkable
   * against the workflow document, and a factory that claimed a gate its
   * workflow did not have would be the more dangerous mistake.
   */
  gate: v.optional(v.nullable(v.string()), null),
  /**
   * What the ticket in the tracker reads while this stage owns the item.
   *
   * The lifecycle a person follows is the ticket's, not reef's, so each stage
   * says which state of the tracker it corresponds to. Null when the stage is
   * invisible to the tracker — pre-flight checks do not move a ticket.
   */
  trackerState: v.optional(v.nullable(v.string()), null),
});
export type FactoryStage = v.InferOutput<typeof FactoryStage>;

/**
 * A factory: a lifecycle, as an ordered list of stages, entered by an event.
 *
 * There can be several, because a team has several lifecycles that deserve
 * automating and they are not one pipeline with branches: a ticket that goes
 * from open to deployed, a red check suite that gets repaired, a Sunday
 * migration across a fleet. Each is its own factory with its own entry event,
 * its own stages and its own gates, and the studio shows each as the thing it
 * is rather than as a loop drawn on an overview.
 *
 * A factory is a row in D1, written through `PUT /api/v1/factory/factories/:id`
 * or the `save_factory` control tool. It used to be `factories/<id>.ts`,
 * imported by the Worker at build time — which meant the only way to add a
 * lifecycle was a deploy, and the agents that run inside the factory could
 * read their own shape and never improve it. The reviewability that bought is
 * not gone, it moved: every write is audited with its actor and its reason,
 * and `revision` catches the stale editor the blob SHA used to catch.
 */
export const FactoryDefinition = v.object({
  id: v.pipe(v.string(), v.regex(/^[a-z0-9-]+$/)),
  name: v.pipe(v.string(), v.minLength(1)),
  description: v.string(),
  /**
   * `delivery` factories move a work item from a signal to a release, and are
   * the ones the Work board is drawn from. `support` factories run beside them
   * — repairing CI, migrating a fleet, learning from incidents — and have no
   * item to place.
   */
  kind: v.picklist(['delivery', 'support']),
  /** What starts an item down this lifecycle. */
  entry: v.object({
    source: SignalSource,
    /** The connection the entry event arrives through, when it is one reef mounts. */
    connectionId: v.optional(v.nullable(v.string()), null),
    description: v.string(),
  }),
  stages: v.pipe(v.array(FactoryStage), v.minLength(1)),
  /** The last stage feeds the first: monitoring re-enters triage, and so on. */
  closesLoop: v.optional(v.boolean(), false),
});
export type FactoryDefinition = v.InferOutput<typeof FactoryDefinition>;
/** The authored form, before defaults are filled in — what a writer sends. */
export type FactoryDocument = v.InferInput<typeof FactoryDefinition>;

/**
 * A factory document that cannot be drawn.
 *
 * Its own class, and not a bare `Error`, because it is a *decision* about the
 * caller's input rather than a fault: `asRefusal` answers 422 with the issues,
 * and an error with no case there is a 500 by definition — which would report
 * reef as broken to somebody who sent a document with two stages claiming one
 * state. That never mattered while factories were imported at build time,
 * because the only reader was `tsc`. It matters now that anyone with workspace
 * admin can `PUT` one.
 */
export class FactoryValidationError extends Error {
  /*
   * Assigned rather than declared as a constructor parameter property. This
   * module is imported by `scripts/*.mjs` under `node --experimental-strip-types`,
   * which refuses `constructor(readonly issues: string[])` outright — and the
   * failure is a parse error three scripts away from anything to do with
   * factories. `WorkflowValidationError` is written the same way for the same
   * reason.
   */
  readonly issues: string[];
  constructor(issues: string[]) {
    super(`invalid factory: ${issues.join('; ')}`);
    this.name = 'FactoryValidationError';
    this.issues = issues;
  }
}

/**
 * Parse and check a factory document.
 *
 * The two checks beyond the schema are the ones reading the document does not
 * catch: a state claimed by two stages puts one work item in two columns at
 * once, and a duplicate stage id makes the second stage unaddressable.
 *
 * Both are collected rather than thrown at the first, so a writer fixing a
 * factory by hand is told everything wrong with it in one round trip instead of
 * discovering the second mistake after fixing the first.
 */
export function parseFactory(input: unknown): FactoryDefinition {
  const parsed = v.safeParse(FactoryDefinition, input);
  if (!parsed.success) {
    throw new FactoryValidationError(
      parsed.issues.map((issue) => {
        const path = issue.path?.map((entry) => String(entry.key)).join('.');
        return path ? `${path}: ${issue.message}` : issue.message;
      }),
    );
  }
  const factory = parsed.output;

  const issues: string[] = [];
  const stageIds = new Set<string>();
  const owners = new Map<WorkItemState, string>();
  for (const stage of factory.stages) {
    if (stageIds.has(stage.id)) {
      issues.push(`factory "${factory.id}" names stage "${stage.id}" twice`);
    }
    stageIds.add(stage.id);

    for (const state of stage.states) {
      const owner = owners.get(state);
      if (owner) {
        issues.push(
          `factory "${factory.id}": state "${state}" is claimed by both "${owner}" and "${stage.id}"`,
        );
      }
      owners.set(state, stage.id);
    }
  }

  if (issues.length) throw new FactoryValidationError(issues);
  return factory;
}

/** Which stage of a factory a work item in `state` is sitting in, if any. */
export function stageForState(
  factory: FactoryDefinition,
  state: WorkItemState,
): FactoryStage | null {
  return factory.stages.find((stage) => stage.states.includes(state)) ?? null;
}
